import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { ChevronDown, Edit, Eye, Plus, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation } from "wouter";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequestV2, parseNormalizedError, queryClient } from "@/lib/queryClient";
import {
  categoryAdminSchema,
  collectionItemAdminSchema,
  eggAdminSchema,
  partnerAdminSchema,
  projectAdminSchema,
  questAdminSchema,
  rarityAdminSchema,
} from "@/schemas/partner-management.schema";
import {
  getFeaturingPartnersOptions,
  getPartnerAdminListOptions,
  getPartnerAdminOptions,
  getPartnerCategoriesOptions,
  getPartnerCollectionItemsOptions,
  getPartnerEggsOptions,
  getPartnerProjectsOptions,
  getPartnerQuestsOptions,
  getPartnerRaritiesOptions,
  getTokenListOptions,
  partnerAdminMutationOptions,
  uploadAdminImage,
} from "@/services/queryOptions/partnerManagementQueryOptions";
import type {
  PartnerAdmin,
  PartnerCategoryAdmin,
  PartnerCollectionItemAdmin,
  PartnerEggAdmin,
  PartnerProject,
  PartnerQuestAdmin,
  PartnerRarityAdmin,
} from "@/types/partner-management";
import type { OuraListParams } from "@/types/oura";
import { AdminDataTable } from "../components/AdminDataTable";
import { partnerAdminKeys } from "@/constant/queryKeys";

type DrawerMode<T> = { type: T; item?: unknown } | null;
type PartnerForm = z.infer<typeof partnerAdminSchema>;
type ProjectForm = z.infer<typeof projectAdminSchema>;
type QuestForm = z.infer<typeof questAdminSchema>;
type EggForm = z.infer<typeof eggAdminSchema>;
type ItemForm = z.infer<typeof collectionItemAdminSchema>;
type RarityForm = z.infer<typeof rarityAdminSchema>;
type CategoryForm = z.infer<typeof categoryAdminSchema>;

const tabItems = [
  { key: "overview", label: "Overview" },
  { key: "projects", label: "Projects" },
  { key: "quests", label: "Quests" },
  { key: "eggs", label: "Eggs" },
  { key: "items", label: "Items" },
  { key: "rarities", label: "Rarities" },
  { key: "categories", label: "Categories" },
  { key: "settings", label: "Settings" },
];

const actionTypes = [
  "show_join_modal",
  "navigate_to_page",
  "share_tweet",
  "external_link",
  "mint_egg_nft",
  "claim_airdrop",
  "upgrade_nft",
];

function toDateTimeInput(value?: string | null) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 16);
}

function toIso(value?: string | null) {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function uploadFolder(...parts: Array<string | number | null | undefined>) {
  return parts
    .map((part) => slugify(String(part ?? "")))
    .filter(Boolean)
    .join("/");
}

async function uploadStagedImage(
  file: File | null | undefined,
  bucket: "partner" | "timeline" | "egg" | "nft" | "rarity" | "token",
  folder: string,
  filenameHint: string,
) {
  if (!file) return undefined;
  const result = await uploadAdminImage(file, bucket, folder, filenameHint);
  return result.objectName;
}

function fieldValue(value: unknown) {
  return value === null || value === undefined || value === "" ? "-" : String(value);
}

function unwrapData<T>(response: T | { data: T }): T {
  return response && typeof response === "object" && "data" in response
    ? (response as { data: T }).data
    : response as T;
}

function isSameFormValue(current: unknown, previous: unknown) {
  return String(current ?? "") === String(previous ?? "");
}

function isSameNumberValue(current: unknown, previous: unknown) {
  const currentNumber = Number(current ?? 0);
  const previousNumber = Number(previous ?? 0);
  return Number.isFinite(currentNumber) && Number.isFinite(previousNumber) && currentNumber === previousNumber;
}

function isSameDateValue(current: string | null | undefined, previous: string | null | undefined) {
  return String(toIso(current) ?? "") === String(previous ? toIso(previous) : "");
}

function isSameNumberArrayValue(current: number[], previous: number[]) {
  if (current.length !== previous.length) return false;
  const sortedCurrent = [...current].sort((a, b) => a - b);
  const sortedPrevious = [...previous].sort((a, b) => a - b);
  return sortedCurrent.every((value, index) => value === sortedPrevious[index]);
}

function isSameJsonValue(current: unknown, previous: unknown) {
  return JSON.stringify(current ?? null) === JSON.stringify(previous ?? null);
}

function detailPath(partnerId: number, tab = "overview") {
  return tab === "overview" ? `/tweeto-admin/partners/${partnerId}` : `/tweeto-admin/partners/${partnerId}/${tab}`;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="break-words text-sm">{value || "-"}</div>
    </div>
  );
}

function BooleanField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox checked={checked} onCheckedChange={(value) => onChange(Boolean(value))} />
      {label}
    </label>
  );
}

function LockTokenNameSelect({
  value,
  onChange,
}: {
  value?: string;
  onChange: (value: string) => void;
}) {
  const tokens = useQuery(getTokenListOptions());
  const selectedToken = value?.trim();
  const hasSelectedToken = Boolean(selectedToken);
  const selectedTokenExists = (tokens.data ?? []).some((token) => token.tokenName === selectedToken);

  return (
    <Select value={selectedToken || "none"} onValueChange={(nextValue) => onChange(nextValue === "none" ? "" : nextValue)}>
      <SelectTrigger>
        <SelectValue placeholder={tokens.isLoading ? "Loading tokens..." : "Select token"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">None</SelectItem>
        {hasSelectedToken && !selectedTokenExists && (
          <SelectItem value={selectedToken!}>{selectedToken}</SelectItem>
        )}
        {(tokens.data ?? []).map((token) => (
          <SelectItem key={token.id} value={token.tokenName}>
            {token.tokenName} - {token.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function TokenSummaryField({
  value,
  onChoose,
}: {
  value?: string;
  onChoose: () => void;
}) {
  const tokens = useQuery(getTokenListOptions());
  const token = (tokens.data ?? []).find((item) => item.tokenName === value);
  const displayValue = token ? `${token.tokenName} - ${token.name}` : value || "";

  return (
    <Field label="Token Name">
      <div className="space-y-1.5">
        <button
          type="button"
          className="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-md border border-input bg-background px-3 text-left text-sm"
          onClick={onChoose}
        >
          <span className={displayValue ? "truncate" : "truncate text-muted-foreground"}>
            {displayValue || "Select token"}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        {token ? (
          <div className="truncate text-xs text-zinc-500">{token.tokenMint} - decimals {token.decimals}</div>
        ) : value ? (
          <div className="text-xs text-zinc-500">Token is not in token list</div>
        ) : null}
      </div>
    </Field>
  );
}

function FeaturedPartnerSummaryField({
  value,
  onChoose,
}: {
  value?: string;
  onChoose: () => void;
}) {
  const partners = useQuery(getFeaturingPartnersOptions());
  const partner = (partners.data ?? []).find((item) => String(item.id) === value);
  const displayValue = partner ? partner.name : value ? `Featured partner ID ${value}` : "";

  return (
    <Field label="Featuring Partner">
      <div className="space-y-1.5">
        <button
          type="button"
          className="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-md border border-input bg-background px-3 text-left text-sm"
          onClick={onChoose}
        >
          <span className={displayValue ? "truncate" : "truncate text-muted-foreground"}>
            {displayValue || "Select featured partner"}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        {partner ? (
          <div className="truncate text-xs text-zinc-500">ID {partner.id}</div>
        ) : value ? (
          <div className="text-xs text-zinc-500">Featured partner was not found in the current list</div>
        ) : null}
      </div>
    </Field>
  );
}

function TokenPickerDialog({
  open,
  value,
  onOpenChange,
  onSelect,
}: {
  open: boolean;
  value?: string;
  onOpenChange: (open: boolean) => void;
  onSelect: (tokenName: string) => void;
}) {
  const tokens = useQuery(getTokenListOptions());
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filteredTokens = (tokens.data ?? []).filter((token) => !query || [
    token.tokenName,
    token.name,
    token.tokenMint,
  ].some((item) => item.toLowerCase().includes(query)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose Token</DialogTitle>
          <DialogDescription>Search by symbol, name, or mint address.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search token..." />
          <div className="max-h-80 overflow-y-auto rounded-md border border-zinc-800">
            {tokens.isLoading ? (
              <div className="p-4 text-sm text-zinc-400">Loading tokens...</div>
            ) : filteredTokens.length === 0 ? (
              <div className="p-4 text-sm text-zinc-400">No token found</div>
            ) : filteredTokens.map((token) => (
              <button
                key={token.id}
                type="button"
                className="flex w-full items-start gap-3 border-b border-zinc-800 p-3 text-left last:border-b-0 hover:bg-zinc-900"
                onClick={() => {
                  onSelect(token.tokenName);
                  onOpenChange(false);
                }}
              >
                {token.image ? <img src={token.image} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="flex h-10 w-10 items-center justify-center rounded bg-zinc-800 text-xs">{token.tokenName.slice(0, 3)}</div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{token.tokenName}</span>
                    {value === token.tokenName && <Badge>Selected</Badge>}
                  </div>
                  <div className="text-sm text-zinc-300">{token.name}</div>
                  <div className="truncate text-xs text-zinc-500">{token.tokenMint}</div>
                  <div className="text-xs text-zinc-500">Decimals: {token.decimals}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PartnerPickerDialog({
  open,
  value,
  onOpenChange,
  onSelect,
}: {
  open: boolean;
  value?: string;
  onOpenChange: (open: boolean) => void;
  onSelect: (partnerId: string) => void;
}) {
  const partners = useQuery(getFeaturingPartnersOptions());
  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const filteredPartners = (partners.data ?? []).filter((partner) => !query || [
      String(partner.id),
      partner.name,
      partner.webUrl ?? "",
      partner.xUrl ?? "",
      partner.discordUrl ?? "",
      partner.telegramUrl ?? "",
      partner.instagramUrl ?? "",
    ].some((item) => item.toLowerCase().includes(query)));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose Featured Partner</DialogTitle>
          <DialogDescription>Search by featured partner name, ID, or social URL.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search featured partner..." />
          <div className="max-h-96 overflow-y-auto rounded-md border border-zinc-800">
            {partners.isLoading ? (
              <div className="p-4 text-sm text-zinc-400">Loading featured partners...</div>
            ) : filteredPartners.length === 0 ? (
              <div className="p-4 text-sm text-zinc-400">No featured partner found</div>
            ) : filteredPartners.map((partner) => (
              <button
                key={partner.id}
                type="button"
                className="flex w-full items-start gap-3 border-b border-zinc-800 p-3 text-left last:border-b-0 hover:bg-zinc-900"
                onClick={() => {
                  onSelect(String(partner.id));
                  onOpenChange(false);
                }}
              >
                {partner.logoUrl ? <img src={partner.logoUrl} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-zinc-800" />}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{partner.name}</span>
                    {value === String(partner.id) && <Badge>Selected</Badge>}
                  </div>
                  <div className="text-xs text-zinc-500">ID {partner.id}</div>
                  <div className="truncate text-xs text-zinc-500">{partner.webUrl || partner.xUrl || partner.instagramUrl || "-"}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ImageUploadField({
  label,
  value,
  onChange,
  file,
  onFileChange,
  accept = ".png,.avif",
}: {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  file?: File | null;
  onFileChange?: (file: File | null) => void;
  bucket: "partner" | "timeline" | "egg" | "nft" | "rarity" | "token";
  accept?: string;
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    setPreviewUrl(nextPreviewUrl);
    onChange(`https://local-upload.tweetonium/${encodeURIComponent(file.name)}`);

    return () => URL.revokeObjectURL(nextPreviewUrl);
  }, [file]);

  const preview = previewUrl || value;

  return (
    <Field label={label}>
      <div className="flex gap-2">
        <Input
          value={value ?? ""}
          onChange={(event) => {
            onFileChange?.(null);
            onChange(event.target.value);
          }}
          placeholder="https://assets.example.com/path/image.png"
        />
        <label className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-input px-3 text-sm">
          <Upload className="mr-2 h-4 w-4" />
          Choose
          <input className="hidden" type="file" accept={accept} onChange={(event) => onFileChange?.(event.target.files?.[0] ?? null)} />
        </label>
      </div>
      {file && <div className="text-xs text-zinc-400">Selected: {file.name}</div>}
      {preview && <img src={preview} alt="" className="h-16 w-16 rounded border border-zinc-800 object-cover" />}
    </Field>
  );
}

function DeleteButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={(event) => {
        event.stopPropagation();
        if (!window.confirm("Delete this record? This action cannot be undone.")) return;
        onClick();
      }}
      aria-label="Delete"
    >
      <Trash2 className="h-4 w-4 text-red-400" />
    </Button>
  );
}

function EditButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={(event) => {
        event.stopPropagation();
        onClick();
      }}
      aria-label="Edit"
    >
      <Edit className="h-4 w-4" />
    </Button>
  );
}

function PartnerDrawer({
  open,
  partner,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  partner?: PartnerAdmin | null;
  onOpenChange: (open: boolean) => void;
  onSaved?: (partner: PartnerAdmin) => void;
}) {
  const { toast } = useToast();
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const [tokenPickerOpen, setTokenPickerOpen] = useState(false);
  const [featuredPickerOpen, setFeaturedPickerOpen] = useState(false);
  const form = useForm<PartnerForm>({
    resolver: zodResolver(partnerAdminSchema),
    values: {
      name: partner?.name ?? "",
      slug: partner?.slug ?? "",
      tokenName: partner?.tokenName ?? "",
      logo: partner?.logo ?? "",
      bannerDesktop: partner?.bannerDesktop ?? "",
      bannerTablet: partner?.bannerTablet ?? "",
      bannerMobile: partner?.bannerMobile ?? "",
      titleDescription: partner?.titleDescription ?? "",
      description: partner?.description ?? "",
      thumbnailImage: partner?.thumbnailImage ?? "",
      thumbnailTitle: partner?.thumbnailTitle ?? "",
      thumbnailDescription: partner?.thumbnailDescription ?? "",
      thumbnailStartDate: toDateTimeInput(partner?.thumbnailStartDate),
      thumbnailEndDate: toDateTimeInput(partner?.thumbnailEndDate),
      webUrl: partner?.webUrl ?? "",
      xUrl: partner?.xUrl ?? "",
      discordUrl: partner?.discordUrl ?? "",
      telegramUrl: partner?.telegramUrl ?? "",
      instagramUrl: partner?.instagramUrl ?? "",
      socialBadgeTweetTemplate: partner?.socialBadgeTweetTemplate ?? "",
      socialBadgeInfoText: partner?.socialBadgeInfoText ?? "",
      maxMintPerUser: String(partner?.maxMintPerUser ?? 1),
      maxMintPerVipUser: String(partner?.maxMintPerVipUser ?? 5),
      isVerified: Boolean(partner?.isVerified),
      isDaoProject: Boolean(partner?.isDaoProject),
      isMarketplaceProject: Boolean(partner?.isMarketplaceProject),
      isTestMode: Boolean(partner?.isTestMode),
      hasFaucet: Boolean(partner?.hasFaucet),
      faucetConfigId: partner?.faucetConfigId ? String(partner.faucetConfigId) : "",
      featuringPartnerId: partner?.featuringPartnerId ? String(partner.featuringPartnerId) : "",
    },
  });
  const mutation = useMutation(
    partnerAdminMutationOptions<unknown>(
      partner ? "PATCH" : "POST",
      partner ? `/api/v1/partner/${partner.id}` : "/api/v1/partner",
    ),
  );

  const submit = form.handleSubmit(async (values) => {
    setUploadingImages(true);
    try {
      const partnerFolder = uploadFolder(values.slug || values.name);
      const [logo, thumbnailImage, bannerDesktop, bannerTablet, bannerMobile] = await Promise.all([
        uploadStagedImage(files.logo, "partner", partnerFolder, "logo"),
        uploadStagedImage(files.thumbnailImage, "partner", partnerFolder, "thumbnail-image"),
        uploadStagedImage(files.bannerDesktop, "partner", partnerFolder, "banner-desktop"),
        uploadStagedImage(files.bannerTablet, "partner", partnerFolder, "banner-tablet"),
        uploadStagedImage(files.bannerMobile, "partner", partnerFolder, "banner-mobile"),
      ]);
      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!partner || changed) payload[key] = value;
      };

      addField("name", values.name, partner ? !isSameFormValue(values.name, partner.name) : true);
      addField("slug", values.slug, partner ? !isSameFormValue(values.slug, partner.slug) : true);
      addField("tokenName", values.tokenName || undefined, partner ? !isSameFormValue(values.tokenName, partner.tokenName) : true);
      addField("titleDescription", values.titleDescription || undefined, partner ? !isSameFormValue(values.titleDescription, partner.titleDescription) : true);
      addField("description", values.description || undefined, partner ? !isSameFormValue(values.description, partner.description) : true);
      addField("thumbnailTitle", values.thumbnailTitle || undefined, partner ? !isSameFormValue(values.thumbnailTitle, partner.thumbnailTitle) : true);
      addField("thumbnailDescription", values.thumbnailDescription || undefined, partner ? !isSameFormValue(values.thumbnailDescription, partner.thumbnailDescription) : true);
      addField("thumbnailStartDate", toIso(values.thumbnailStartDate) ?? null, partner ? !isSameDateValue(values.thumbnailStartDate, partner.thumbnailStartDate) : true);
      addField("thumbnailEndDate", toIso(values.thumbnailEndDate) ?? null, partner ? !isSameDateValue(values.thumbnailEndDate, partner.thumbnailEndDate) : true);
      addField("webUrl", values.webUrl || undefined, partner ? !isSameFormValue(values.webUrl, partner.webUrl) : true);
      addField("xUrl", values.xUrl || undefined, partner ? !isSameFormValue(values.xUrl, partner.xUrl) : true);
      addField("discordUrl", values.discordUrl || undefined, partner ? !isSameFormValue(values.discordUrl, partner.discordUrl) : true);
      addField("telegramUrl", values.telegramUrl || undefined, partner ? !isSameFormValue(values.telegramUrl, partner.telegramUrl) : true);
      addField("instagramUrl", values.instagramUrl || undefined, partner ? !isSameFormValue(values.instagramUrl, partner.instagramUrl) : true);
      addField("socialBadgeTweetTemplate", values.socialBadgeTweetTemplate || undefined, partner ? !isSameFormValue(values.socialBadgeTweetTemplate, partner.socialBadgeTweetTemplate) : true);
      addField("socialBadgeInfoText", values.socialBadgeInfoText || undefined, partner ? !isSameFormValue(values.socialBadgeInfoText, partner.socialBadgeInfoText) : true);
      addField("maxMintPerUser", Number(values.maxMintPerUser), partner ? !isSameNumberValue(values.maxMintPerUser, partner.maxMintPerUser) : true);
      addField("maxMintPerVipUser", Number(values.maxMintPerVipUser), partner ? !isSameNumberValue(values.maxMintPerVipUser, partner.maxMintPerVipUser) : true);
      addField("isVerified", values.isVerified, partner ? values.isVerified !== Boolean(partner.isVerified) : true);
      addField("isDaoProject", values.isDaoProject, partner ? values.isDaoProject !== Boolean(partner.isDaoProject) : true);
      addField("isMarketplaceProject", values.isMarketplaceProject, partner ? values.isMarketplaceProject !== Boolean(partner.isMarketplaceProject) : true);
      addField("isTestMode", values.isTestMode, partner ? values.isTestMode !== Boolean(partner.isTestMode) : true);
      addField("hasFaucet", values.hasFaucet, partner ? values.hasFaucet !== Boolean(partner.hasFaucet) : true);
      addField("faucetConfigId", values.faucetConfigId ? Number(values.faucetConfigId) : null, partner ? !isSameNumberValue(values.faucetConfigId, partner.faucetConfigId) : true);
      addField("featuringPartnerId", values.featuringPartnerId ? Number(values.featuringPartnerId) : null, partner ? !isSameNumberValue(values.featuringPartnerId, partner.featuringPartnerId) : true);

      if (logo !== undefined || (!partner && values.logo)) {
        payload.logo = logo ?? values.logo;
      } else if (partner && !isSameFormValue(values.logo, partner.logo)) {
        payload.logo = values.logo;
      }

      if (thumbnailImage !== undefined || (!partner && values.thumbnailImage)) {
        payload.thumbnailImage = (thumbnailImage ?? values.thumbnailImage) || undefined;
      } else if (partner && !isSameFormValue(values.thumbnailImage, partner.thumbnailImage)) {
        payload.thumbnailImage = values.thumbnailImage || undefined;
      }

      if (bannerDesktop !== undefined || (!partner && values.bannerDesktop)) {
        payload.bannerDesktop = bannerDesktop ?? values.bannerDesktop;
      } else if (partner && !isSameFormValue(values.bannerDesktop, partner.bannerDesktop)) {
        payload.bannerDesktop = values.bannerDesktop;
      }

      if (bannerTablet !== undefined || (!partner && values.bannerTablet)) {
        payload.bannerTablet = bannerTablet ?? values.bannerTablet;
      } else if (partner && !isSameFormValue(values.bannerTablet, partner.bannerTablet)) {
        payload.bannerTablet = values.bannerTablet;
      }

      if (bannerMobile !== undefined || (!partner && values.bannerMobile)) {
        payload.bannerMobile = bannerMobile ?? values.bannerMobile;
      } else if (partner && !isSameFormValue(values.bannerMobile, partner.bannerMobile)) {
        payload.bannerMobile = values.bannerMobile;
      }

      if (partner && Object.keys(payload).length === 0) {
        toast({ title: "No changes to update" });
        onOpenChange(false);
        return;
      }

      mutation.mutate(payload, {
        onSuccess: (response) => {
          setFiles({});
          const savedPartner = unwrapData(response as PartnerAdmin | { data: PartnerAdmin });
          toast({ title: partner ? "Partner updated" : "Partner created" });
          onSaved?.(savedPartner);
          onOpenChange(false);
        },
      });
    } catch (error) {
      toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    } finally {
      setUploadingImages(false);
    }
  });

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>{partner ? "Edit Partner" : "Add Partner"}</SheetTitle>
          </SheetHeader>
          <form className="mt-6 grid gap-4 md:grid-cols-2" onSubmit={submit}>
          <Field label="Name">
            <Input {...form.register("name")} onBlur={() => !form.getValues("slug") && form.setValue("slug", slugify(form.getValues("name")))} placeholder="Tweetonium DAO" />
          </Field>
          <Field label="Slug">
            <Input {...form.register("slug")} placeholder="tweetonium-dao" />
          </Field>
          <TokenSummaryField
            value={form.watch("tokenName")}
            onChoose={() => setTokenPickerOpen(true)}
          />
          <FeaturedPartnerSummaryField
            value={form.watch("featuringPartnerId")}
            onChoose={() => setFeaturedPickerOpen(true)}
          />
          <ImageUploadField label="Logo" value={form.watch("logo")} onChange={(value) => form.setValue("logo", value)} file={files.logo} onFileChange={(file) => setFiles((current) => ({ ...current, logo: file }))} bucket="partner" />
          <ImageUploadField label="Thumbnail Image" value={form.watch("thumbnailImage")} onChange={(value) => form.setValue("thumbnailImage", value)} file={files.thumbnailImage} onFileChange={(file) => setFiles((current) => ({ ...current, thumbnailImage: file }))} bucket="partner" accept=".avif" />
          <ImageUploadField label="Banner Desktop" value={form.watch("bannerDesktop")} onChange={(value) => form.setValue("bannerDesktop", value)} file={files.bannerDesktop} onFileChange={(file) => setFiles((current) => ({ ...current, bannerDesktop: file }))} bucket="partner" />
          <ImageUploadField label="Banner Tablet" value={form.watch("bannerTablet")} onChange={(value) => form.setValue("bannerTablet", value)} file={files.bannerTablet} onFileChange={(file) => setFiles((current) => ({ ...current, bannerTablet: file }))} bucket="partner" />
          <ImageUploadField label="Banner Mobile" value={form.watch("bannerMobile")} onChange={(value) => form.setValue("bannerMobile", value)} file={files.bannerMobile} onFileChange={(file) => setFiles((current) => ({ ...current, bannerMobile: file }))} bucket="partner" />
          <Field label="Thumbnail Title">
            <Input {...form.register("thumbnailTitle")} placeholder="Season live now" />
          </Field>
          <Field label="Thumbnail Start">
            <Input type="datetime-local" {...form.register("thumbnailStartDate")} />
          </Field>
          <Field label="Thumbnail End">
            <Input type="datetime-local" {...form.register("thumbnailEndDate")} />
          </Field>
          <Field label="Website">
            <Input {...form.register("webUrl")} placeholder="https://example.com" />
          </Field>
          <Field label="X URL">
            <Input {...form.register("xUrl")} placeholder="https://x.com/example" />
          </Field>
          <Field label="Discord URL">
            <Input {...form.register("discordUrl")} placeholder="https://discord.gg/example" />
          </Field>
          <Field label="Telegram URL">
            <Input {...form.register("telegramUrl")} placeholder="https://t.me/example" />
          </Field>
          <Field label="Instagram URL">
            <Input {...form.register("instagramUrl")} placeholder="https://instagram.com/example" />
          </Field>
          <Field label="Max Mint/User">
            <Input {...form.register("maxMintPerUser")} placeholder="1" />
          </Field>
          <Field label="Max Mint/VIP">
            <Input {...form.register("maxMintPerVipUser")} placeholder="5" />
          </Field>
          <div className="space-y-3 md:col-span-2">
            <BooleanField label="Verified" checked={form.watch("isVerified")} onChange={(value) => form.setValue("isVerified", value)} />
            <BooleanField label="DAO Project" checked={form.watch("isDaoProject")} onChange={(value) => form.setValue("isDaoProject", value)} />
            <BooleanField label="Marketplace Project" checked={form.watch("isMarketplaceProject")} onChange={(value) => form.setValue("isMarketplaceProject", value)} />
            <BooleanField label="Test Mode" checked={form.watch("isTestMode")} onChange={(value) => form.setValue("isTestMode", value)} />
            <BooleanField label="Has Faucet" checked={form.watch("hasFaucet")} onChange={(value) => form.setValue("hasFaucet", value)} />
          </div>
          <Field label="Title Description">
            <Textarea {...form.register("titleDescription")} placeholder="Short partner headline" />
          </Field>
          <Field label="Thumbnail Description">
            <Textarea {...form.register("thumbnailDescription")} placeholder="Short thumbnail copy" />
          </Field>
          <Field label="Description">
            <Textarea {...form.register("description")} placeholder="Full partner description" />
          </Field>
          <Field label="Social Badge Tweet Template">
            <Textarea {...form.register("socialBadgeTweetTemplate")} placeholder="I joined {partner} on Tweetonium" />
          </Field>
          <Field label="Social Badge Info Text">
            <Textarea {...form.register("socialBadgeInfoText")} placeholder="Badge claim information" />
          </Field>
            <Button className="md:col-span-2" disabled={mutation.isPending || uploadingImages}>{partner ? "Update Partner" : "Create Partner"}</Button>
          </form>
        </SheetContent>
      </Sheet>
      <TokenPickerDialog
        open={tokenPickerOpen}
        value={form.watch("tokenName")}
        onOpenChange={setTokenPickerOpen}
        onSelect={(tokenName) => form.setValue("tokenName", tokenName)}
      />
      <PartnerPickerDialog
        open={featuredPickerOpen}
        value={form.watch("featuringPartnerId")}
        onOpenChange={setFeaturedPickerOpen}
        onSelect={(partnerId) => form.setValue("featuringPartnerId", partnerId)}
      />
    </>
  );
}

function ProjectDrawer({
  partnerId,
  partnerSlug,
  item,
  eggs,
  open,
  onOpenChange,
}: {
  partnerId: number;
  partnerSlug: string;
  item?: PartnerProject | null;
  eggs: PartnerEggAdmin[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const tokens = useQuery(getTokenListOptions());
  const form = useForm<ProjectForm>({
    resolver: zodResolver(projectAdminSchema),
    defaultValues: {
      title: item?.title ?? "",
      description: item?.description ?? "",
      image: item?.image ?? "",
      nftCollection: item?.nftCollection ?? "",
      tokenListId: item?.tokenListId ? String(item.tokenListId) : "",
      eggId: item?.eggId ? String(item.eggId) : "",
      startDate: toDateTimeInput(item?.startDate) || toDateTimeInput(new Date().toISOString()),
      endDate: toDateTimeInput(item?.endDate) || toDateTimeInput(new Date(Date.now() + 86400000).toISOString()),
      isActive: item?.isActive ?? true,
      isEggMechanism: item?.isEggMechanism ?? false,
      isVipSession: item?.isVipSession ?? false,
      isUsingWhitelist: item?.isUsingWhitelist ?? false,
      isNeedJoinDao: item?.isNeedJoinDao ?? false,
      isNeedVerificationCode: item?.isNeedVerificationCode ?? false,
    },
  });

  useEffect(() => {
    form.reset({
      title: item?.title ?? "",
      description: item?.description ?? "",
      image: item?.image ?? "",
      nftCollection: item?.nftCollection ?? "",
      tokenListId: item?.tokenListId ? String(item.tokenListId) : "",
      eggId: item?.eggId ? String(item.eggId) : "",
      startDate: toDateTimeInput(item?.startDate) || toDateTimeInput(new Date().toISOString()),
      endDate: toDateTimeInput(item?.endDate) || toDateTimeInput(new Date(Date.now() + 86400000).toISOString()),
      isActive: item?.isActive ?? true,
      isEggMechanism: item?.isEggMechanism ?? false,
      isVipSession: item?.isVipSession ?? false,
      isUsingWhitelist: item?.isUsingWhitelist ?? false,
      isNeedJoinDao: item?.isNeedJoinDao ?? false,
      isNeedVerificationCode: item?.isNeedVerificationCode ?? false,
    });
    setImageFile(null);
  }, [form, item]);

  const mutation = useMutation(partnerAdminMutationOptions<unknown>(
    item ? "PATCH" : "POST",
    item ? `/api/v1/partner/${partnerId}/timeline/${item.id}` : `/api/v1/partner/${partnerId}/timeline`,
  ));

  const submit = form.handleSubmit(async (values) => {
    setUploadingImages(true);
    try {
      const image = await uploadStagedImage(imageFile, "timeline", uploadFolder(partnerSlug), values.title || "project");
      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!item || changed) payload[key] = value;
      };

      addField("title", values.title, item ? !isSameFormValue(values.title, item.title) : true);
      addField("description", values.description || null, item ? !isSameFormValue(values.description, item.description) : true);
      addField("nftCollection", values.nftCollection || null, item ? !isSameFormValue(values.nftCollection, item.nftCollection) : true);
      addField("tokenListId", values.tokenListId ? Number(values.tokenListId) : null, item ? !isSameNumberValue(values.tokenListId, item.tokenListId) : true);
      addField("eggId", values.eggId ? Number(values.eggId) : null, item ? !isSameNumberValue(values.eggId, item.eggId) : true);
      addField("startDate", toIso(values.startDate), item ? !isSameDateValue(values.startDate, item.startDate) : true);
      addField("endDate", toIso(values.endDate), item ? !isSameDateValue(values.endDate, item.endDate) : true);
      addField("isActive", values.isActive, item ? values.isActive !== Boolean(item.isActive) : true);
      addField("isEggMechanism", values.isEggMechanism, item ? values.isEggMechanism !== Boolean(item.isEggMechanism) : true);
      addField("isVipSession", values.isVipSession, item ? values.isVipSession !== Boolean(item.isVipSession) : true);
      addField("isUsingWhitelist", values.isUsingWhitelist, item ? values.isUsingWhitelist !== Boolean(item.isUsingWhitelist) : true);
      addField("isNeedJoinDao", values.isNeedJoinDao, item ? values.isNeedJoinDao !== Boolean(item.isNeedJoinDao) : true);
      addField("isNeedVerificationCode", values.isNeedVerificationCode, item ? values.isNeedVerificationCode !== Boolean(item.isNeedVerificationCode) : true);

      if (image !== undefined || (!item && values.image)) {
        payload.image = (image ?? values.image) || null;
      } else if (item && !isSameFormValue(values.image, item.image)) {
        payload.image = values.image || null;
      }

      if (item && Object.keys(payload).length === 0) {
        toast({ title: "No changes to update" });
        onOpenChange(false);
        return;
      }

      mutation.mutate(payload, {
        onSuccess: () => {
          setImageFile(null);
          toast({ title: item ? "Project updated" : "Project created" });
          onOpenChange(false);
        },
      });
    } catch (error) {
      toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    } finally {
      setUploadingImages(false);
    }
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader><SheetTitle>{item ? "Edit Project" : "Add Project"}</SheetTitle></SheetHeader>
        <form className="mt-6 grid gap-4" onSubmit={submit}>
          <Field label="Title"><Input {...form.register("title")} placeholder="Season 1 Drop" /></Field>
          <ImageUploadField label="Image" value={form.watch("image") ?? ""} onChange={(value) => form.setValue("image", value)} file={imageFile} onFileChange={setImageFile} bucket="timeline" />
          <Field label="NFT Collection"><Input {...form.register("nftCollection")} placeholder="Collection mint address" /></Field>
          <Field label="Token">
            <Select value={form.watch("tokenListId") || "none"} onValueChange={(value) => form.setValue("tokenListId", value === "none" ? "" : value)}>
              <SelectTrigger><SelectValue placeholder={tokens.isLoading ? "Loading tokens..." : "Select token"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {(tokens.data ?? []).map((token) => (
                  <SelectItem key={token.id} value={String(token.id)}>
                    {token.tokenName} - {token.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Egg">
            <Select value={form.watch("eggId") || "none"} onValueChange={(value) => form.setValue("eggId", value === "none" ? "" : value)}>
              <SelectTrigger><SelectValue placeholder="Select egg" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {eggs.map((egg) => <SelectItem key={egg.id} value={String(egg.id)}>{egg.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Start Date"><Input type="datetime-local" {...form.register("startDate")} /></Field>
          <Field label="End Date"><Input type="datetime-local" {...form.register("endDate")} /></Field>
          <div className="grid gap-3 rounded border border-zinc-800 p-3">
            <BooleanField label="Active" checked={form.watch("isActive")} onChange={(value) => form.setValue("isActive", value)} />
            <BooleanField label="Egg Mechanism" checked={form.watch("isEggMechanism")} onChange={(value) => form.setValue("isEggMechanism", value)} />
            <BooleanField label="VIP Session" checked={form.watch("isVipSession")} onChange={(value) => form.setValue("isVipSession", value)} />
            <BooleanField label="Using Whitelist" checked={form.watch("isUsingWhitelist")} onChange={(value) => form.setValue("isUsingWhitelist", value)} />
            <BooleanField label="Need Join DAO" checked={form.watch("isNeedJoinDao")} onChange={(value) => form.setValue("isNeedJoinDao", value)} />
            <BooleanField label="Need Verification Code" checked={form.watch("isNeedVerificationCode")} onChange={(value) => form.setValue("isNeedVerificationCode", value)} />
          </div>
          <Field label="Description"><Textarea {...form.register("description")} placeholder="Project description" /></Field>
          <Button disabled={mutation.isPending || uploadingImages}>{item ? "Update Project" : "Create Project"}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function QuestDrawer({ partnerId, partnerSlug, item, open, onOpenChange }: { partnerId: number; partnerSlug: string; item?: PartnerQuestAdmin | null; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const form = useForm<QuestForm>({
    resolver: zodResolver(questAdminSchema),
    values: {
      title: item?.title ?? "",
      description: item?.description ?? "",
      image: item?.image ?? "",
      pointsReward: String(item?.pointsReward ?? 100),
      buttonText: item?.buttonText ?? "Complete Quest",
      actionType: item?.actionType ?? "external_link",
      actionParams: item?.actionParams ? JSON.stringify(item.actionParams, null, 2) : "",
      scrapingConfigType: item?.scrapingConfigType ?? "",
      startDate: toDateTimeInput(item?.startDate) || toDateTimeInput(new Date().toISOString()),
      endDate: toDateTimeInput(item?.endDate) || toDateTimeInput(new Date(Date.now() + 86400000).toISOString()),
      ordering: String(item?.ordering ?? 0),
      isActive: item?.isActive ?? true,
    },
  });
  const mutation = useMutation(partnerAdminMutationOptions<unknown>(item ? "PUT" : "POST", item ? `/api/v1/quest/${item.id}` : "/api/v1/quest"));
  const submit = form.handleSubmit(async (values) => {
    let params: unknown = null;
    if (values.actionParams) {
      try {
        params = JSON.parse(values.actionParams);
      } catch {
        form.setError("actionParams", { message: "Use valid JSON" });
        return;
      }
    }
    setUploadingImages(true);
    try {
      const image = await uploadStagedImage(imageFile, "partner", uploadFolder(partnerSlug, "quest"), values.title || "quest");
      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!item || changed) payload[key] = value;
      };

      addField("partnerId", partnerId, !item);
      addField("title", values.title, item ? !isSameFormValue(values.title, item.title) : true);
      addField("description", values.description || null, item ? !isSameFormValue(values.description, item.description) : true);
      addField("pointsReward", Number(values.pointsReward), item ? !isSameNumberValue(values.pointsReward, item.pointsReward) : true);
      addField("buttonText", values.buttonText, item ? !isSameFormValue(values.buttonText, item.buttonText) : true);
      addField("actionType", values.actionType, item ? !isSameFormValue(values.actionType, item.actionType) : true);
      addField("actionParams", params, item ? !isSameJsonValue(params, item.actionParams) : true);
      addField("scrapingConfigType", values.scrapingConfigType || null, item ? !isSameFormValue(values.scrapingConfigType, item.scrapingConfigType) : true);
      addField("startDate", toIso(values.startDate), item ? !isSameDateValue(values.startDate, item.startDate) : true);
      addField("endDate", toIso(values.endDate), item ? !isSameDateValue(values.endDate, item.endDate) : true);
      addField("ordering", Number(values.ordering), item ? !isSameNumberValue(values.ordering, item.ordering) : true);
      addField("isActive", values.isActive, item ? values.isActive !== Boolean(item.isActive) : true);

      if (image !== undefined || (!item && values.image)) {
        payload.image = (image ?? values.image) || null;
      } else if (item && !isSameFormValue(values.image, item.image)) {
        payload.image = values.image || null;
      }

      if (item && Object.keys(payload).length === 0) {
        toast({ title: "No changes to update" });
        onOpenChange(false);
        return;
      }

      mutation.mutate(payload, { onSuccess: () => { setImageFile(null); toast({ title: item ? "Quest updated" : "Quest created" }); onOpenChange(false); } });
    } catch (error) {
      toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    } finally {
      setUploadingImages(false);
    }
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader><SheetTitle>{item ? "Edit Quest" : "Add Quest"}</SheetTitle></SheetHeader>
        <form className="mt-6 grid gap-4" onSubmit={submit}>
          <Field label="Title"><Input {...form.register("title")} placeholder="Follow us on X" /></Field>
          <ImageUploadField label="Image" value={form.watch("image") ?? ""} onChange={(value) => form.setValue("image", value)} file={imageFile} onFileChange={setImageFile} bucket="partner" />
          <Field label="Points Reward"><Input {...form.register("pointsReward")} placeholder="100" /></Field>
          <Field label="Button Text"><Input {...form.register("buttonText")} placeholder="Complete Quest" /></Field>
          <Field label="Action Type">
            <Select value={form.watch("actionType")} onValueChange={(value) => form.setValue("actionType", value)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{actionTypes.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Action Params JSON"><Textarea {...form.register("actionParams")} placeholder='{"type":"external_link","url":"https://example.com"}' /></Field>
          <Field label="Scraping Config Type"><Input {...form.register("scrapingConfigType")} placeholder="twitter_follow" /></Field>
          <Field label="Start Date"><Input type="datetime-local" {...form.register("startDate")} /></Field>
          <Field label="End Date"><Input type="datetime-local" {...form.register("endDate")} /></Field>
          <Field label="Ordering"><Input {...form.register("ordering")} placeholder="0" /></Field>
          <BooleanField label="Active" checked={form.watch("isActive")} onChange={(value) => form.setValue("isActive", value)} />
          <Field label="Description"><Textarea {...form.register("description")} placeholder="Quest description" /></Field>
          <Button disabled={mutation.isPending || uploadingImages}>{item ? "Update Quest" : "Create Quest"}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function RarityDialog({ partnerId, partnerSlug, item, open, onOpenChange, onSaved }: { partnerId: number; partnerSlug: string; item?: PartnerRarityAdmin | null; open: boolean; onOpenChange: (open: boolean) => void; onSaved?: (rarity: PartnerRarityAdmin) => void }) {
  const { toast } = useToast();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const form = useForm<RarityForm>({
    resolver: zodResolver(rarityAdminSchema),
    values: {
      title: item?.title ?? "",
      name: item?.name ?? "",
      rarityChance: String(item?.rarityChance ?? "1"),
      image: item?.image ?? "",
    },
  });
  useEffect(() => {
    setImageFile(null);
  }, [item, open]);
  const mutation = useMutation(partnerAdminMutationOptions<unknown, { data: PartnerRarityAdmin }>(item ? "PUT" : "POST", item ? `/api/v1/rarity/${item.id}` : "/api/v1/rarity"));
  const submit = form.handleSubmit(async (values) => {
    setUploadingImages(true);
    try {
      const image = await uploadStagedImage(imageFile, "rarity", uploadFolder(partnerSlug), values.name || values.title || "rarity");
      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!item || changed) payload[key] = value;
      };

      addField("partnerId", partnerId, !item);
      addField("title", values.title, item ? !isSameFormValue(values.title, item.title) : true);
      addField("name", values.name, item ? !isSameFormValue(values.name, item.name) : true);
      addField("rarityChance", values.rarityChance, item ? !isSameNumberValue(values.rarityChance, item.rarityChance) : true);

      if (image !== undefined || (!item && values.image)) {
        payload.image = (image ?? values.image) || null;
      } else if (item && !isSameFormValue(values.image, item.image)) {
        payload.image = values.image || null;
      }

      if (item && Object.keys(payload).length === 0) {
        toast({ title: "No changes to update" });
        onOpenChange(false);
        return;
      }

      mutation.mutate(payload, {
        onSuccess: (result) => { setImageFile(null); toast({ title: item ? "Rarity updated" : "Rarity created" }); onSaved?.(result.data); onOpenChange(false); },
      });
    } catch (error) {
      toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    } finally {
      setUploadingImages(false);
    }
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader><SheetTitle>{item ? "Edit Rarity" : "Create Rarity"}</SheetTitle></SheetHeader>
        <form className="mt-6 space-y-4" onSubmit={submit}>
          <Field label="Title"><Input {...form.register("title")} placeholder="Legendary" /></Field>
          <Field label="Name"><Input {...form.register("name")} placeholder="legendary" /></Field>
          <Field label="Chance"><Input {...form.register("rarityChance")} placeholder="5" /></Field>
          <ImageUploadField label="Image" value={form.watch("image")} onChange={(value) => form.setValue("image", value)} file={imageFile} onFileChange={setImageFile} bucket="rarity" />
          <Button disabled={mutation.isPending || uploadingImages}>{item ? "Update Rarity" : "Create Rarity"}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function CategoryDialog({ partnerId, item, open, onOpenChange, onSaved }: { partnerId: number; item?: PartnerCategoryAdmin | null; open: boolean; onOpenChange: (open: boolean) => void; onSaved?: (category: PartnerCategoryAdmin) => void }) {
  const { toast } = useToast();
  const form = useForm<CategoryForm>({ resolver: zodResolver(categoryAdminSchema), values: { name: item?.name ?? "" } });
  const mutation = useMutation(partnerAdminMutationOptions<unknown, { data: PartnerCategoryAdmin }>(item ? "PUT" : "POST", item ? `/api/v1/category/${item.id}` : "/api/v1/category"));
  const submit = form.handleSubmit((values) => {
    const payload: Record<string, unknown> = {};
    if (!item) payload.partnerId = partnerId;
    if (!item || !isSameFormValue(values.name, item.name)) payload.name = values.name;

    if (item && Object.keys(payload).length === 0) {
      toast({ title: "No changes to update" });
      onOpenChange(false);
      return;
    }

    mutation.mutate(payload, {
      onSuccess: (result) => { toast({ title: item ? "Category updated" : "Category created" }); onSaved?.(result.data); onOpenChange(false); },
    });
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md">
        <SheetHeader><SheetTitle>{item ? "Edit Category" : "Create Category"}</SheetTitle></SheetHeader>
        <form className="mt-6 space-y-4" onSubmit={submit}>
          <Field label="Name"><Input {...form.register("name")} placeholder="Avatar" /></Field>
          <Button disabled={mutation.isPending}>{item ? "Update Category" : "Create Category"}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function EggDrawer({ partnerId, partnerSlug, item, rarities, open, onOpenChange }: { partnerId: number; partnerSlug: string; item?: PartnerEggAdmin | null; rarities: PartnerRarityAdmin[]; open: boolean; onOpenChange: (open: boolean) => void }) {
  const { toast } = useToast();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const form = useForm<EggForm>({
    resolver: zodResolver(eggAdminSchema),
    defaultValues: {
      name: item?.name ?? "",
      description: item?.description ?? "",
      imageUrl: item?.image ?? "",
      rarityIds: item?.rarityIds ?? [],
      contractAddress: item?.contractUrl ?? "",
      maxSupply: String(item?.maxMinted ?? 100),
      minted: String(item?.minted ?? 0),
      price: String(item?.price ?? 0),
      mintFee: String(item?.mintFee ?? 0),
      protocolFee: String(item?.protocolFee ?? 0),
      priorityFeeTncUrl: item?.priorityFeeTncUrl ?? "",
      termsAndConditions: item?.termsAndConditions ?? "",
      lockToken: String(item?.lockToken ?? 0),
      lockTokenName: item?.lockTokenName ?? "",
    },
  });
  useEffect(() => {
    form.reset({
      name: item?.name ?? "",
      description: item?.description ?? "",
      imageUrl: item?.image ?? "",
      rarityIds: item?.rarityIds ?? [],
      contractAddress: item?.contractUrl ?? "",
      maxSupply: String(item?.maxMinted ?? 100),
      minted: String(item?.minted ?? 0),
      price: String(item?.price ?? 0),
      mintFee: String(item?.mintFee ?? 0),
      protocolFee: String(item?.protocolFee ?? 0),
      priorityFeeTncUrl: item?.priorityFeeTncUrl ?? "",
      termsAndConditions: item?.termsAndConditions ?? "",
      lockToken: String(item?.lockToken ?? 0),
      lockTokenName: item?.lockTokenName ?? "",
    });
  }, [form, item]);

  const mutation = useMutation(partnerAdminMutationOptions<unknown>(item ? "PATCH" : "POST", item ? `/api/v1/egg/${item.id}` : "/api/v1/egg"));
  const submit = form.handleSubmit(async (values) => {
    setUploadingImages(true);
    try {
      const imageUrl = await uploadStagedImage(imageFile, "egg", uploadFolder(partnerSlug), values.name || "egg");
      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!item || changed) payload[key] = value;
      };

      addField("partnerId", partnerId, !item);
      addField("name", values.name, item ? !isSameFormValue(values.name, item.name) : true);
      addField("description", values.description || null, item ? !isSameFormValue(values.description, item.description) : true);
      addField("rarityIds", values.rarityIds, item ? !isSameNumberArrayValue(values.rarityIds, item.rarityIds) : true);
      addField("contractAddress", values.contractAddress || null, item ? !isSameFormValue(values.contractAddress, item.contractUrl) : true);
      addField("maxSupply", Number(values.maxSupply), item ? !isSameNumberValue(values.maxSupply, item.maxMinted) : true);
      addField("minted", Number(values.minted ?? 0), item ? !isSameNumberValue(values.minted, item.minted) : false);
      addField("price", Number(values.price), item ? !isSameNumberValue(values.price, item.price) : true);
      addField("mintFee", Number(values.mintFee), item ? !isSameNumberValue(values.mintFee, item.mintFee) : true);
      addField("protocolFee", Number(values.protocolFee), item ? !isSameNumberValue(values.protocolFee, item.protocolFee) : true);
      addField("priorityFeeTncUrl", values.priorityFeeTncUrl || null, item ? !isSameFormValue(values.priorityFeeTncUrl, item.priorityFeeTncUrl) : true);
      addField("termsAndConditions", values.termsAndConditions || null, item ? !isSameFormValue(values.termsAndConditions, item.termsAndConditions) : true);
      addField("lockToken", Number(values.lockToken), item ? !isSameNumberValue(values.lockToken, item.lockToken) : true);
      addField("lockTokenName", values.lockTokenName || null, item ? !isSameFormValue(values.lockTokenName, item.lockTokenName) : true);

      if (imageUrl !== undefined || (!item && values.imageUrl)) {
        payload.imageUrl = imageUrl ?? values.imageUrl;
      } else if (item && !isSameFormValue(values.imageUrl, item.image)) {
        payload.imageUrl = values.imageUrl;
      }

      if (item && Object.keys(payload).length === 0) {
        toast({ title: "No changes to update" });
        onOpenChange(false);
        return;
      }

      mutation.mutate(payload, { onSuccess: () => { setImageFile(null); toast({ title: item ? "Egg updated" : "Egg created" }); onOpenChange(false); } });
    } catch (error) {
      toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    } finally {
      setUploadingImages(false);
    }
  });
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader><SheetTitle>{item ? "Edit Egg" : "Add Egg"}</SheetTitle></SheetHeader>
        <form className="mt-6 grid gap-4" onSubmit={submit}>
          <Field label="Name"><Input {...form.register("name")} placeholder="Genesis Egg" /></Field>
          <ImageUploadField label="Image" value={form.watch("imageUrl")} onChange={(value) => form.setValue("imageUrl", value)} file={imageFile} onFileChange={setImageFile} bucket="egg" />
          <Field label="Rarities">
            <div className="grid gap-2 rounded border border-zinc-800 p-3">
              {rarities.map((rarity) => {
                const selected = form.watch("rarityIds").includes(rarity.id);
                return <BooleanField key={rarity.id} label={`${rarity.title} (${rarity.name})`} checked={selected} onChange={(checked) => form.setValue("rarityIds", checked ? [...form.getValues("rarityIds"), rarity.id] : form.getValues("rarityIds").filter((id) => id !== rarity.id))} />;
              })}
            </div>
          </Field>
          <Field label="Max Minted"><Input {...form.register("maxSupply")} placeholder="1000" /></Field>
          {item && <Field label="Minted"><Input {...form.register("minted")} placeholder="0" /></Field>}
          <Field label="Price"><Input {...form.register("price")} placeholder="0" /></Field>
          <Field label="Mint Fee"><Input {...form.register("mintFee")} placeholder="0.05" /></Field>
          <Field label="Protocol Fee"><Input {...form.register("protocolFee")} placeholder="0.01" /></Field>
          <Field label="Lock Token"><Input {...form.register("lockToken")} placeholder="0" /></Field>
          <Field label="Lock Token Name">
            <LockTokenNameSelect value={form.watch("lockTokenName") || item?.lockTokenName || ""} onChange={(value) => form.setValue("lockTokenName", value)} />
          </Field>
          <Field label="Priority Fee T&C URL"><Input {...form.register("priorityFeeTncUrl")} placeholder="https://example.com/terms" /></Field>
          <Field label="Description"><Textarea {...form.register("description")} /></Field>
          <Field label="Terms"><Textarea {...form.register("termsAndConditions")} /></Field>
          <Button disabled={mutation.isPending || uploadingImages}>{item ? "Update Egg" : "Create Egg"}</Button>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function CollectionItemDrawer({
  partnerId,
  partnerSlug,
  item,
  projects,
  eggs,
  rarities,
  categories,
  open,
  onOpenChange,
}: {
  partnerId: number;
  partnerSlug: string;
  item?: PartnerCollectionItemAdmin | null;
  projects: PartnerProject[];
  eggs: PartnerEggAdmin[];
  rarities: PartnerRarityAdmin[];
  categories: PartnerCategoryAdmin[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [rarityOpen, setRarityOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [uploadingImages, setUploadingImages] = useState(false);
  const form = useForm<ItemForm>({
    resolver: zodResolver(collectionItemAdminSchema),
    defaultValues: {
      projectId: item ? String(item.projectId) : "",
      name: item?.name ?? "",
      description: item?.description ?? "",
      image: item?.image ?? "",
      type: item?.type ?? "nft",
      rarityId: item ? String(item.rarityId) : "",
      categoryId: item ? String(item.categoryId) : "",
      eggId: item?.eggId ? String(item.eggId) : "",
      traitStackingConfigId: item?.traitStackingConfigId ? String(item.traitStackingConfigId) : "",
      baseImageUrl: item?.baseImageUrl ?? "",
      contractUrl: item?.contractUrl ?? "",
      thumbnail: item?.thumbnail ?? "",
      maxMinted: String(item?.maxMinted ?? 100),
      lockToken: String(item?.lockToken ?? 0),
      lockTokenFee: String(item?.lockTokenFee ?? 0),
      lockTokenName: item?.lockTokenName ?? "",
      nftCollection: item?.nftCollection ?? "",
      price: String(item?.price ?? 0),
      mintFee: String(item?.mintFee ?? 0),
      protocolFee: String(item?.protocolFee ?? 0),
      priorityFeeTncUrl: item?.priorityFeeTncUrl ?? "",
    },
  });
  useEffect(() => {
    form.reset({
      projectId: item ? String(item.projectId) : "",
      name: item?.name ?? "",
      description: item?.description ?? "",
      image: item?.image ?? "",
      type: item?.type ?? "nft",
      rarityId: item ? String(item.rarityId) : "",
      categoryId: item ? String(item.categoryId) : "",
      eggId: item?.eggId ? String(item.eggId) : "",
      traitStackingConfigId: item?.traitStackingConfigId ? String(item.traitStackingConfigId) : "",
      baseImageUrl: item?.baseImageUrl ?? "",
      contractUrl: item?.contractUrl ?? "",
      thumbnail: item?.thumbnail ?? "",
      maxMinted: String(item?.maxMinted ?? 100),
      lockToken: String(item?.lockToken ?? 0),
      lockTokenFee: String(item?.lockTokenFee ?? 0),
      lockTokenName: item?.lockTokenName ?? "",
      nftCollection: item?.nftCollection ?? "",
      price: String(item?.price ?? 0),
      mintFee: String(item?.mintFee ?? 0),
      protocolFee: String(item?.protocolFee ?? 0),
      priorityFeeTncUrl: item?.priorityFeeTncUrl ?? "",
    });
  }, [form, item]);

  const mutation = useMutation(partnerAdminMutationOptions<unknown>(item ? "PUT" : "POST", item ? `/api/v1/collection-item/${item.id}` : "/api/v1/collection-item"));
  const submit = form.handleSubmit(async (values) => {
    setUploadingImages(true);
    try {
      const [image, thumbnail, baseImageUrl] = await Promise.all([
        uploadStagedImage(files.image, "nft", uploadFolder(partnerSlug, "nft"), values.name || "nft"),
        uploadStagedImage(files.thumbnail, "nft", uploadFolder(partnerSlug, "thumbnail-nft"), `${values.name || "nft"}-thumbnail`),
        uploadStagedImage(files.baseImageUrl, "nft", uploadFolder(partnerSlug, "base-image"), `${values.name || "nft"}-base`),
      ]);

      const payload: Record<string, unknown> = {};
      const addField = (key: string, value: unknown, changed: boolean) => {
        if (!item || changed) payload[key] = value;
      };

      addField("projectId", Number(values.projectId), item ? !isSameNumberValue(values.projectId, item.projectId) : true);
      addField("name", values.name, item ? !isSameFormValue(values.name, item.name) : true);
      addField("description", values.description || null, item ? !isSameFormValue(values.description, item.description) : true);
      addField("type", values.type, item ? !isSameFormValue(values.type, item.type) : true);
      addField("rarityId", Number(values.rarityId), item ? !isSameNumberValue(values.rarityId, item.rarityId) : true);
      addField("categoryId", Number(values.categoryId), item ? !isSameNumberValue(values.categoryId, item.categoryId) : true);
      addField("eggId", values.eggId ? Number(values.eggId) : null, item ? !isSameNumberValue(values.eggId, item.eggId) : true);
      addField("traitStackingConfigId", values.traitStackingConfigId ? Number(values.traitStackingConfigId) : null, item ? !isSameNumberValue(values.traitStackingConfigId, item.traitStackingConfigId) : true);
      addField("contractUrl", values.contractUrl || null, item ? !isSameFormValue(values.contractUrl, item.contractUrl) : true);
      addField("maxMinted", Number(values.maxMinted), item ? !isSameNumberValue(values.maxMinted, item.maxMinted) : true);
      addField("lockToken", Number(values.lockToken), item ? !isSameNumberValue(values.lockToken, item.lockToken) : true);
      addField("lockTokenFee", Number(values.lockTokenFee), item ? !isSameNumberValue(values.lockTokenFee, item.lockTokenFee) : true);
      addField("lockTokenName", values.lockTokenName || null, item ? !isSameFormValue(values.lockTokenName, item.lockTokenName) : true);
      addField("nftCollection", values.nftCollection || null, item ? !isSameFormValue(values.nftCollection, item.nftCollection) : true);
      addField("price", Number(values.price), item ? !isSameNumberValue(values.price, item.price) : true);
      addField("mintFee", Number(values.mintFee), item ? !isSameNumberValue(values.mintFee, item.mintFee) : true);
      addField("protocolFee", Number(values.protocolFee), item ? !isSameNumberValue(values.protocolFee, item.protocolFee) : true);
      addField("priorityFeeTncUrl", values.priorityFeeTncUrl || null, item ? !isSameFormValue(values.priorityFeeTncUrl, item.priorityFeeTncUrl) : true);

      if (image !== undefined || (!item && values.image)) {
        payload.image = image ?? values.image;
      } else if (item && !isSameFormValue(values.image, item.image)) {
        payload.image = values.image;
      }

      if (baseImageUrl !== undefined || (!item && values.baseImageUrl)) {
        payload.baseImageUrl = (baseImageUrl ?? values.baseImageUrl) || null;
      } else if (item && !isSameFormValue(values.baseImageUrl, item.baseImageUrl)) {
        payload.baseImageUrl = values.baseImageUrl || null;
      }

      if (thumbnail !== undefined || (!item && values.thumbnail)) {
        payload.thumbnail = (thumbnail ?? values.thumbnail) || null;
      } else if (item && !isSameFormValue(values.thumbnail, item.thumbnail)) {
        payload.thumbnail = values.thumbnail || null;
      }

      if (item && Object.keys(payload).length === 0) {
        toast({ title: "No changes to update" });
        onOpenChange(false);
        return;
      }

      mutation.mutate(payload, { onSuccess: () => { setFiles({}); toast({ title: item ? "Collection item updated" : "Collection item created" }); onOpenChange(false); } });
    } catch (error) {
      toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Unknown error", variant: "destructive" });
    } finally {
      setUploadingImages(false);
    }
  });
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
          <SheetHeader><SheetTitle>{item ? "Edit Collection Item" : "Add Collection Item"}</SheetTitle></SheetHeader>
          <form className="mt-6 grid gap-4" onSubmit={submit}>
            {item && (
              <>
                <Field label="ID"><Input value={item.id} disabled /></Field>
                <Field label="Public ID"><Input value={item.idPublic} disabled /></Field>
              </>
            )}
            <Field label="Project">
              <Select value={form.watch("projectId")} onValueChange={(value) => form.setValue("projectId", value)}>
                <SelectTrigger><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((project) => <SelectItem key={project.id} value={String(project.id)}>{project.title}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Name"><Input {...form.register("name")} placeholder="Genesis Avatar" /></Field>
            <ImageUploadField label="Image" value={form.watch("image")} onChange={(value) => form.setValue("image", value)} file={files.image} onFileChange={(file) => setFiles((current) => ({ ...current, image: file }))} bucket="nft" accept=".png" />
            <Field label="Rarity">
              <div className="flex gap-2">
                <Select value={form.watch("rarityId")} onValueChange={(value) => form.setValue("rarityId", value)}>
                  <SelectTrigger><SelectValue placeholder="Select rarity" /></SelectTrigger>
                  <SelectContent>{rarities.map((rarity) => <SelectItem key={rarity.id} value={String(rarity.id)}>{rarity.title}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={() => setRarityOpen(true)}><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>
            <Field label="Category">
              <div className="flex gap-2">
                <Select value={form.watch("categoryId")} onValueChange={(value) => form.setValue("categoryId", value)}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>{categories.map((category) => <SelectItem key={category.id} value={String(category.id)}>{category.name}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={() => setCategoryOpen(true)}><Plus className="h-4 w-4" /></Button>
              </div>
            </Field>
            <Field label="Egg">
              <Select value={form.watch("eggId") || "none"} onValueChange={(value) => form.setValue("eggId", value === "none" ? "" : value)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {eggs.map((egg) => <SelectItem key={egg.id} value={String(egg.id)}>{egg.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Type"><Input {...form.register("type")} placeholder="nft" /></Field>
            <Field label="Trait Stacking Config ID"><Input {...form.register("traitStackingConfigId")} placeholder="Optional config ID" /></Field>
            <ImageUploadField label="Base Image URL" value={form.watch("baseImageUrl") ?? ""} onChange={(value) => form.setValue("baseImageUrl", value)} file={files.baseImageUrl} onFileChange={(file) => setFiles((current) => ({ ...current, baseImageUrl: file }))} bucket="nft" />
            <Field label="Contract URL"><Input {...form.register("contractUrl")} placeholder="https://example.com/contract" /></Field>
            <ImageUploadField label="Thumbnail" value={form.watch("thumbnail") ?? ""} onChange={(value) => form.setValue("thumbnail", value)} file={files.thumbnail} onFileChange={(file) => setFiles((current) => ({ ...current, thumbnail: file }))} bucket="nft" />
            <Field label="Max Minted"><Input {...form.register("maxMinted")} placeholder="100" /></Field>
            <Field label="Lock Token"><Input {...form.register("lockToken")} placeholder="0" /></Field>
            <Field label="Lock Token Fee"><Input {...form.register("lockTokenFee")} placeholder="0" /></Field>
            <Field label="Lock Token Name">
              <LockTokenNameSelect value={form.watch("lockTokenName") || item?.lockTokenName || ""} onChange={(value) => form.setValue("lockTokenName", value)} />
            </Field>
            <Field label="NFT Collection"><Input {...form.register("nftCollection")} placeholder="Collection identifier" /></Field>
            <Field label="Price"><Input {...form.register("price")} placeholder="0" /></Field>
            <Field label="Mint Fee"><Input {...form.register("mintFee")} placeholder="0.05" /></Field>
            <Field label="Protocol Fee"><Input {...form.register("protocolFee")} placeholder="0.01" /></Field>
            <Field label="Priority Fee T&C URL"><Input {...form.register("priorityFeeTncUrl")} placeholder="https://example.com/terms" /></Field>
            <Field label="Description"><Textarea {...form.register("description")} /></Field>
            <Button disabled={mutation.isPending || uploadingImages}>{item ? "Update Collection Item" : "Create Collection Item"}</Button>
          </form>
        </SheetContent>
      </Sheet>
      <RarityDialog partnerId={partnerId} partnerSlug={partnerSlug} open={rarityOpen} onOpenChange={setRarityOpen} onSaved={(rarity) => form.setValue("rarityId", String(rarity.id))} />
      <CategoryDialog partnerId={partnerId} open={categoryOpen} onOpenChange={setCategoryOpen} onSaved={(category) => form.setValue("categoryId", String(category.id))} />
    </>
  );
}

function PartnerListPage() {
  const [, setLocation] = useLocation();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const partners = useQuery(getPartnerAdminListOptions({ search: params.search }));
  const columns = useMemo<ColumnDef<PartnerAdmin>[]>(() => [
    { accessorKey: "name", header: "Name", cell: ({ row }) => <div className="font-medium">{row.original.name}</div> },
    { accessorKey: "slug", header: "Slug" },
    { accessorKey: "isVerified", header: "Verified", cell: ({ row }) => row.original.isVerified ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge> },
    { id: "actions", header: "Actions", cell: ({ row }) => <Button variant="ghost" size="icon" onClick={(event) => { event.stopPropagation(); setLocation(detailPath(row.original.id)); }}><Eye className="h-4 w-4" /></Button> },
  ], [setLocation]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Partners</h2>
          <p className="text-sm text-zinc-400">Manage partner profiles, projects, quests, eggs, and collection items.</p>
        </div>
      </div>
      <AdminDataTable
        columns={columns}
        data={partners.data ?? []}
        total={partners.data?.length ?? 0}
        params={params}
        onParamsChange={setParams}
        exportName="partners.csv"
        searchPlaceholder="Search partners..."
        actionButton={<Button onClick={() => setDrawerOpen(true)}><Plus className="mr-2 h-4 w-4" />Add Partner</Button>}
        onRowClick={(partner) => setLocation(detailPath(partner.id))}
      />
      <PartnerDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </div>
  );
}

function OverviewTab({ partner }: { partner: PartnerAdmin }) {
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardHeader><CardTitle>Partner Information</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <DetailField label="Name" value={partner.name} />
          <DetailField label="Slug" value={partner.slug} />
          <DetailField label="Token" value={partner.tokenName} />
          <DetailField label="Website" value={partner.webUrl} />
          <DetailField label="X" value={partner.xUrl} />
          <DetailField label="Discord" value={partner.discordUrl} />
          <DetailField label="Description" value={partner.description} />
          <DetailField label="Social Badge Info" value={partner.socialBadgeInfoText} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Stats</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <DetailField label="Members" value={partner.totalMember ?? 0} />
          <DetailField label="NFTs Minted" value={partner.totalNftsMinted ?? 0} />
          <DetailField label="Distributed Points" value={partner.totalDistributedPoints ?? 0} />
          <DetailField label="Max Mint/User" value={partner.maxMintPerUser} />
          <DetailField label="Max Mint/VIP" value={partner.maxMintPerVipUser} />
        </CardContent>
      </Card>
    </div>
  );
}

function ProjectsTab({ partnerId, partnerSlug, eggs }: { partnerId: number; partnerSlug: string; eggs: PartnerEggAdmin[] }) {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawer, setDrawer] = useState<PartnerProject | null | undefined>();
  const projects = useQuery(getPartnerProjectsOptions(partnerId));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/partner/${partnerId}/timeline/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Project deleted" });
    },
    onError: (error) => {
      const normalized = parseNormalizedError(error);
      const details = normalized.details && typeof normalized.details === "object"
        ? Object.entries(normalized.details as Record<string, number>)
            .filter(([, count]) => Number(count) > 0)
            .map(([key, count]) => `${key}: ${count}`)
            .join(", ")
        : "";
      toast({
        title: "Cannot delete project",
        description: details ? `${normalized.message} (${details})` : normalized.message,
        variant: "destructive",
      });
    },
  });
  const rows = (projects.data ?? []).filter((project) => !params.search || project.title.toLowerCase().includes(params.search.toLowerCase()));
  const columns = useMemo<ColumnDef<PartnerProject>[]>(() => [
    { accessorKey: "title", header: "Name" },
    { accessorKey: "image", header: "Image", cell: ({ row }) => row.original.image ? <img src={row.original.image} alt="" className="h-10 w-10 rounded object-cover" /> : "-" },
    { accessorKey: "description", header: "Description", cell: ({ row }) => <span className="line-clamp-1">{row.original.description ?? "-"}</span> },
    { accessorKey: "eggId", header: "Egg", cell: ({ row }) => row.original.eggId ?? "-" },
    { accessorKey: "startDate", header: "Start", cell: ({ row }) => new Date(row.original.startDate).toLocaleString() },
    { accessorKey: "endDate", header: "End", cell: ({ row }) => new Date(row.original.endDate).toLocaleString() },
    { accessorKey: "isActive", header: "Status", cell: ({ row }) => row.original.isActive ? <Badge>Active</Badge> : <Badge variant="secondary">Inactive</Badge> },
    { id: "actions", header: "Actions", cell: ({ row }) => <div className="flex gap-1"><EditButton onClick={() => setDrawer(row.original)} /><DeleteButton onClick={() => del.mutate(row.original.id)} /></div> },
  ], [del]);
  return (
    <>
      <AdminDataTable columns={columns} data={rows} total={rows.length} params={params} onParamsChange={setParams} exportName="partner-projects.csv" actionButton={<Button onClick={() => setDrawer(null)}><Plus className="mr-2 h-4 w-4" />Add Project</Button>} />
      <ProjectDrawer partnerId={partnerId} partnerSlug={partnerSlug} item={drawer || null} eggs={eggs} open={drawer !== undefined} onOpenChange={(open) => !open && setDrawer(undefined)} />
    </>
  );
}

function QuestsTab({ partnerId, partnerSlug }: { partnerId: number; partnerSlug: string }) {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawer, setDrawer] = useState<PartnerQuestAdmin | null | undefined>();
  const quests = useQuery(getPartnerQuestsOptions(partnerId));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/quest/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Quest deleted" });
    },
  });
  const rows = (quests.data ?? []).filter((quest) => !params.search || quest.title.toLowerCase().includes(params.search.toLowerCase()));
  const columns = useMemo<ColumnDef<PartnerQuestAdmin>[]>(() => [
    { accessorKey: "title", header: "Title" },
    { accessorKey: "pointsReward", header: "Points" },
    { accessorKey: "actionType", header: "Action" },
    { accessorKey: "isActive", header: "Active", cell: ({ row }) => row.original.isActive ? <Badge>Yes</Badge> : <Badge variant="secondary">No</Badge> },
    { accessorKey: "ordering", header: "Ordering" },
    { id: "actions", header: "Actions", cell: ({ row }) => <div className="flex gap-1"><EditButton onClick={() => setDrawer(row.original)} /><DeleteButton onClick={() => del.mutate(row.original.id)} /></div> },
  ], [del, toast]);
  return (
    <>
      <AdminDataTable columns={columns} data={rows} total={rows.length} params={params} onParamsChange={setParams} exportName="quests.csv" actionButton={<Button onClick={() => setDrawer(null)}><Plus className="mr-2 h-4 w-4" />Add Quest</Button>} />
      <QuestDrawer partnerId={partnerId} partnerSlug={partnerSlug} item={drawer || null} open={drawer !== undefined} onOpenChange={(open) => !open && setDrawer(undefined)} />
    </>
  );
}

function EggsTab({ partnerId, partnerSlug, rarities }: { partnerId: number; partnerSlug: string; rarities: PartnerRarityAdmin[] }) {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawer, setDrawer] = useState<PartnerEggAdmin | null | undefined>();
  const eggs = useQuery(getPartnerEggsOptions(partnerId));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/egg/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Egg deleted" });
    },
  });
  const rows = (eggs.data ?? []).filter((egg) => !params.search || egg.name.toLowerCase().includes(params.search.toLowerCase()));
  const columns = useMemo<ColumnDef<PartnerEggAdmin>[]>(() => [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "image", header: "Image", cell: ({ row }) => <img src={row.original.image} alt="" className="h-10 w-10 rounded object-cover" /> },
    { accessorKey: "maxMinted", header: "Max" },
    { accessorKey: "minted", header: "Minted" },
    { accessorKey: "price", header: "Price" },
    { accessorKey: "collectionItemCount", header: "Pool" },
    { id: "actions", header: "Actions", cell: ({ row }) => <div className="flex gap-1"><EditButton onClick={() => setDrawer(row.original)} /><DeleteButton onClick={() => del.mutate(row.original.id)} /></div> },
  ], [del]);
  return (
    <>
      <AdminDataTable columns={columns} data={rows} total={rows.length} params={params} onParamsChange={setParams} exportName="eggs.csv" actionButton={<Button onClick={() => setDrawer(null)}><Plus className="mr-2 h-4 w-4" />Add Egg</Button>} />
      <EggDrawer partnerId={partnerId} partnerSlug={partnerSlug} item={drawer || null} rarities={rarities} open={drawer !== undefined} onOpenChange={(open) => !open && setDrawer(undefined)} />
    </>
  );
}

function ItemsTab({ partnerId, partnerSlug, projects, eggs, rarities, categories }: { partnerId: number; partnerSlug: string; projects: PartnerProject[]; eggs: PartnerEggAdmin[]; rarities: PartnerRarityAdmin[]; categories: PartnerCategoryAdmin[] }) {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawer, setDrawer] = useState<PartnerCollectionItemAdmin | null | undefined>();
  const items = useQuery(getPartnerCollectionItemsOptions(partnerId));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/collection-item/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Collection item deleted" });
    },
  });
  const rows = (items.data ?? []).filter((item) => !params.search || item.name.toLowerCase().includes(params.search.toLowerCase()));
  const columns = useMemo<ColumnDef<PartnerCollectionItemAdmin>[]>(() => [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "image", header: "Image", cell: ({ row }) => <img src={row.original.image} alt="" className="h-10 w-10 rounded object-cover" /> },
    { accessorKey: "projectName", header: "Project" },
    { accessorKey: "rarityName", header: "Rarity" },
    { accessorKey: "categoryName", header: "Category" },
    { accessorKey: "eggName", header: "Egg", cell: ({ row }) => row.original.eggName ?? "-" },
    { id: "minted", header: "Minted", cell: ({ row }) => `${row.original.currentMinted}/${row.original.maxMinted}` },
    { accessorKey: "price", header: "Price" },
    { id: "actions", header: "Actions", cell: ({ row }) => <div className="flex gap-1"><EditButton onClick={() => setDrawer(row.original)} /><DeleteButton onClick={() => del.mutate(row.original.id)} /></div> },
  ], [del]);
  return (
    <>
      <AdminDataTable columns={columns} data={rows} total={rows.length} params={params} onParamsChange={setParams} exportName="collection-items.csv" actionButton={<Button onClick={() => setDrawer(null)}><Plus className="mr-2 h-4 w-4" />Add Collection Item</Button>} />
      <CollectionItemDrawer partnerId={partnerId} partnerSlug={partnerSlug} item={drawer || null} projects={projects} eggs={eggs} rarities={rarities} categories={categories} open={drawer !== undefined} onOpenChange={(open) => !open && setDrawer(undefined)} />
    </>
  );
}

function RaritiesTab({ partnerId, partnerSlug }: { partnerId: number; partnerSlug: string }) {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawer, setDrawer] = useState<PartnerRarityAdmin | null | undefined>();
  const rarities = useQuery(getPartnerRaritiesOptions(partnerId));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/rarity/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Rarity deleted" });
    },
  });
  const rows = (rarities.data ?? []).filter((rarity) =>
    !params.search ||
    [rarity.title, rarity.name].some((value) => value.toLowerCase().includes(params.search!.toLowerCase())),
  );
  const columns = useMemo<ColumnDef<PartnerRarityAdmin>[]>(() => [
    { accessorKey: "title", header: "Title" },
    { accessorKey: "name", header: "Name" },
    { accessorKey: "image", header: "Image", cell: ({ row }) => row.original.image ? <img src={row.original.image} alt="" className="h-10 w-10 rounded object-cover" /> : "-" },
    { accessorKey: "rarityChance", header: "Chance" },
    { accessorKey: "createdAt", header: "Created", cell: ({ row }) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : "-" },
    { id: "actions", header: "Actions", cell: ({ row }) => <div className="flex gap-1"><EditButton onClick={() => setDrawer(row.original)} /><DeleteButton onClick={() => del.mutate(row.original.id)} /></div> },
  ], [del]);

  return (
    <>
      <AdminDataTable columns={columns} data={rows} total={rows.length} params={params} onParamsChange={setParams} exportName="rarities.csv" actionButton={<Button onClick={() => setDrawer(null)}><Plus className="mr-2 h-4 w-4" />Add Rarity</Button>} />
      <RarityDialog partnerId={partnerId} partnerSlug={partnerSlug} item={drawer || null} open={drawer !== undefined} onOpenChange={(open) => !open && setDrawer(undefined)} />
    </>
  );
}

function CategoriesTab({ partnerId }: { partnerId: number }) {
  const { toast } = useToast();
  const [params, setParams] = useState<OuraListParams>({ page: 1, page_size: 25, order: "asc" });
  const [drawer, setDrawer] = useState<PartnerCategoryAdmin | null | undefined>();
  const categories = useQuery(getPartnerCategoriesOptions(partnerId));
  const del = useMutation({
    mutationFn: (id: number) => apiRequestV2("DELETE", `/api/v1/category/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Category deleted" });
    },
  });
  const rows = (categories.data ?? []).filter((category) => !params.search || category.name.toLowerCase().includes(params.search.toLowerCase()));
  const columns = useMemo<ColumnDef<PartnerCategoryAdmin>[]>(() => [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "createdAt", header: "Created", cell: ({ row }) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleString() : "-" },
    { id: "actions", header: "Actions", cell: ({ row }) => <div className="flex gap-1"><EditButton onClick={() => setDrawer(row.original)} /><DeleteButton onClick={() => del.mutate(row.original.id)} /></div> },
  ], [del]);

  return (
    <>
      <AdminDataTable columns={columns} data={rows} total={rows.length} params={params} onParamsChange={setParams} exportName="categories.csv" actionButton={<Button onClick={() => setDrawer(null)}><Plus className="mr-2 h-4 w-4" />Add Category</Button>} />
      <CategoryDialog partnerId={partnerId} item={drawer || null} open={drawer !== undefined} onOpenChange={(open) => !open && setDrawer(undefined)} />
    </>
  );
}

function SettingsTab({ partner }: { partner: PartnerAdmin }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteBlockers, setDeleteBlockers] = useState<Record<string, number> | null>(null);
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const deleteMutation = useMutation({
    mutationFn: () => apiRequestV2<{ message: string }>("DELETE", `/api/v1/partner/${partner.id}`),
    onSuccess: () => {
      setDeleteBlockers(null);
      setConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: partnerAdminKeys.all });
      toast({ title: "Partner deleted", description: `${partner.name} has been deleted.` });
      setLocation("/tweeto-admin/partners");
    },
    onError: (error) => {
      const normalized = parseNormalizedError(error);
      const details = normalized.details && typeof normalized.details === "object"
        ? normalized.details as Record<string, number>
        : null;
      setDeleteBlockers(details);
      toast({
        title: "Cannot delete partner",
        description: normalized.message,
        variant: "destructive",
      });
    },
  });
  const blockerEntries = Object.entries(deleteBlockers ?? {}).filter(([, count]) => Number(count) > 0);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader><CardTitle>General Settings</CardTitle></CardHeader>
        <CardContent className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="font-medium">{partner.name}</div>
            <div className="text-sm text-zinc-400">{partner.slug}</div>
          </div>
          <Button onClick={() => setDrawerOpen(true)}><Edit className="mr-2 h-4 w-4" />Edit Partner</Button>
        </CardContent>
      </Card>
      <Card className="border-red-900/50">
        <CardHeader><CardTitle>Danger Zone</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-zinc-400">
            Delete this partner only if it has no related projects, quests, assets, users, whitelist entries, admin assignments, or points.
          </div>
          {blockerEntries.length > 0 && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
              <div className="mb-2 font-medium">Delete is blocked by related data:</div>
              <div className="grid gap-1 sm:grid-cols-2">
                {blockerEntries.map(([key, count]) => (
                  <div key={key} className="flex justify-between gap-3">
                    <span>{key}</span>
                    <span className="font-mono">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button
            variant="destructive"
            onClick={() => {
              setDeleteBlockers(null);
              setConfirmOpen(true);
            }}
            disabled={deleteMutation.isPending}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Partner
          </Button>
        </CardContent>
      </Card>
      <PartnerDrawer partner={partner} open={drawerOpen} onOpenChange={setDrawerOpen} />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="border-red-900/50">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {partner.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the partner only when no blocking related data exists. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete Partner"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PartnerDetailPage({ partnerId, tab }: { partnerId: number; tab: string }) {
  const [, setLocation] = useLocation();
  const [editOpen, setEditOpen] = useState(false);
  const partner = useQuery(getPartnerAdminOptions(partnerId));
  const projects = useQuery(getPartnerProjectsOptions(partnerId));
  const rarities = useQuery(getPartnerRaritiesOptions(partnerId));
  const categories = useQuery(getPartnerCategoriesOptions(partnerId));
  const eggs = useQuery(getPartnerEggsOptions(partnerId));

  if (!partner.data) {
    return <div className="text-sm text-zinc-400">Loading partner...</div>;
  }

  const activeTab = tabItems.some((item) => item.key === tab) ? tab : "overview";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-500">
            <Link href="/tweeto-admin/partners" className="hover:text-zinc-200">
              Partners
            </Link>
            <span>/</span>
            <Link href={detailPath(partnerId)} className="hover:text-zinc-200">
              {partner.data.name}
            </Link>
            <span>/</span>
            <span>{activeTab}</span>
          </div>
          <div className="mt-2 flex items-center gap-3">
            <h2 className="text-2xl font-semibold">{partner.data.name}</h2>
            {partner.data.isVerified && <Badge>Verified</Badge>}
            {partner.data.isTestMode && <Badge variant="secondary">Test</Badge>}
          </div>
        </div>
        <Button onClick={() => setEditOpen(true)}><Edit className="mr-2 h-4 w-4" />Edit</Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
        <nav className="space-y-1">
          {tabItems.map((item) => (
            <button
              key={item.key}
              className={`flex h-10 w-full items-center rounded px-3 text-left text-sm ${activeTab === item.key ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"}`}
              onClick={() => setLocation(detailPath(partnerId, item.key))}
            >
              {item.label}
            </button>
          ))}
        </nav>
        <div>
          {activeTab === "overview" && <OverviewTab partner={partner.data} />}
          {activeTab === "projects" && <ProjectsTab partnerId={partnerId} partnerSlug={partner.data.slug} eggs={eggs.data ?? []} />}
          {activeTab === "quests" && <QuestsTab partnerId={partnerId} partnerSlug={partner.data.slug} />}
          {activeTab === "eggs" && <EggsTab partnerId={partnerId} partnerSlug={partner.data.slug} rarities={rarities.data ?? []} />}
          {activeTab === "items" && <ItemsTab partnerId={partnerId} partnerSlug={partner.data.slug} projects={projects.data ?? []} eggs={eggs.data ?? []} rarities={rarities.data ?? []} categories={categories.data ?? []} />}
          {activeTab === "rarities" && <RaritiesTab partnerId={partnerId} partnerSlug={partner.data.slug} />}
          {activeTab === "categories" && <CategoriesTab partnerId={partnerId} />}
          {activeTab === "settings" && <SettingsTab partner={partner.data} />}
        </div>
      </div>
      <PartnerDrawer partner={partner.data} open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
}

export function PartnerManagementPage() {
  const [location] = useLocation();
  const match = location.match(/^\/tweeto-admin\/partners\/(\d+)(?:\/([^/]+))?/);
  if (!match) return <PartnerListPage />;

  const partnerId = Number(match[1]);
  return <PartnerDetailPage partnerId={partnerId} tab={match[2] ?? "overview"} />;
}
